import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Send, Heart, Sparkles } from "lucide-react";

const SHARE_KEY = "oracoes_compartilhadas";
const MAX_SHARES = 3;

function loadShared() {
  try {
    const raw = localStorage.getItem(SHARE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

export const ShareOracao = ({ number, title }) => {
  const [shared, setShared] = useState([]);
  const [landingUrl, setLandingUrl] = useState("");

  useEffect(() => {
    setShared(loadShared());
    api
      .get("/config")
      .then((res) => {
        const base = (res.data?.public_base_url || window.location.origin).replace(/\/+$/, "");
        setLandingUrl(base);
      })
      .catch(() => setLandingUrl(window.location.origin));
  }, []);

  const alreadyShared = shared.includes(number);
  const limitReached = !alreadyShared && shared.length >= MAX_SHARES;
  const remaining = Math.max(0, MAX_SHARES - shared.length);

  const message = useMemo(() => {
    const base = (landingUrl || window.location.origin).replace(/\/+$/, "");
    const link = `${base}/?ir=oracao`;
    return (
      `🕊️ *${title}*\n\n` +
      `Encontrei uma oração que tocou o meu coração e precisei compartilhar com você. 💛\n\n` +
      `Ela faz parte de *"Quando Faltam Palavras — 50 Orações para Falar com Deus"*.\n\n` +
      `Ouça e sinta esse momento com Deus também:\n${link}`
    );
  }, [title, landingUrl]);

  const handleShare = () => {
    if (limitReached) return;
    if (!alreadyShared) {
      const updated = [...shared, number];
      setShared(updated);
      try {
        localStorage.setItem(SHARE_KEY, JSON.stringify(updated));
      } catch (e) {
        /* falha silenciosa */
      }
    }
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      data-testid="share-oracao"
      className="w-full bg-[#FAF7F2] rounded-3xl p-6 sm:p-8 border border-[#E5DCCF] shadow-sm mt-6 fade-up delay-2"
    >
      <div className="flex items-center gap-2.5 mb-1.5">
        <Sparkles className="w-5 h-5 text-[#C5A059]" strokeWidth={1.8} />
        <h3
          data-testid="share-oracao-title"
          className="font-serif-display text-xl sm:text-2xl text-[#3A2921] font-semibold"
        >
          Abençoe alguém com esta oração
        </h3>
      </div>

      <p className="text-xs text-[#9C877A] mb-4 leading-relaxed">
        Envie esta oração para alguém especial pelo WhatsApp. Você pode presentear até{" "}
        <span className="font-semibold text-[#6E5A4F]">{MAX_SHARES} orações</span>.
      </p>

      {limitReached ? (
        <div
          data-testid="share-limit-message"
          className="rounded-2xl border border-[#E5DCCF] bg-white/60 px-5 py-4 text-center"
        >
          <p className="flex items-center justify-center gap-1.5 font-serif-display text-base text-[#6E5A4F]">
            <Heart className="w-4 h-4 text-[#C5A059]" fill="currentColor" strokeWidth={0} />
            Você já compartilhou suas 3 orações 💛
          </p>
          <p className="text-xs text-[#9C877A] mt-1.5">
            Que elas alcancem quem mais precisa neste momento.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            data-testid="share-oracao-button"
            onClick={handleShare}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#4C7A5A] px-7 py-3 text-sm font-medium text-[#F7F2E9] tracking-wide hover:bg-[#3f6a4d] active:scale-[0.98] transition-all shadow-sm"
          >
            <Send className="w-4 h-4" strokeWidth={2} />
            {alreadyShared ? "Compartilhar novamente" : "Compartilhar no WhatsApp"}
          </button>
          <span data-testid="share-remaining" className="text-xs text-[#B8A899]">
            {alreadyShared
              ? "Você já presenteou esta oração"
              : `${remaining} ${remaining === 1 ? "compartilhamento restante" : "compartilhamentos restantes"}`}
          </span>
        </div>
      )}
    </section>
  );
};
